import { createContext, useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { DEALERSHIP_CONFIG } from "../config/dealershipConfig";
import { getSalespeopleByDealership } from "../services/dealership";
const DealershipContext = createContext({});

export const DealershipProvider = ({ children }) => {
  const location = useLocation();
  const webURL = new URL(window.location.href);

  const getDealershipHostName = () => {
    const hostname = webURL.hostname;
    const hostNameParts = hostname.split(".");
    return hostNameParts[0] ? hostNameParts[0] : null;
  };

  const getUrlDealershipId = () => {
    const dealershipHostName = getDealershipHostName();
    return dealershipHostName && dealershipHostName in DEALERSHIP_CONFIG
      ? dealershipHostName
      : location.pathname.split("/")[1];
  };

  const getBaseURL = () => {
    const dealershipHostName = getDealershipHostName();
    return dealershipHostName && dealershipHostName in DEALERSHIP_CONFIG
      ? "/"
      : `/${location.pathname.split("/")[1]}/`;
  };

  const [baseURL, setBaseURL] = useState(() => getBaseURL());
  const [dealershipId, setDealershipId] = useState(getUrlDealershipId());
  const [salesPersonInfo, setSalesPersonInfo] = useState(["", "", ""]);
  const [dealershipName, setDealershipName] = useState(
    DEALERSHIP_CONFIG[getUrlDealershipId()]?.name
  );
  const [dealershipImgUrl, setDealershipImgUrl] = useState(
    DEALERSHIP_CONFIG[getUrlDealershipId()]?.imgUrl
  );
  const [linkMatchesToVDP, setLinkMatchesToVdp] = useState(
    DEALERSHIP_CONFIG[getUrlDealershipId()]?.linkMatchesToVDP
  );
  const [defaultInventoryImage, setDefaultInventoryImage] = useState(
    DEALERSHIP_CONFIG[getUrlDealershipId()]?.defaultInventoryImage
  );
  const [userSource, setUserSource] = useState("Walk-In");
  const [initContextFlag, setInitContextFlag] = useState(false);
  const [testDrivePreApprovalFormEnabled, setTestDrivePreApprovalFormEnabled] =
    useState(
      DEALERSHIP_CONFIG[getUrlDealershipId()]?.testDrivePreApprovalFormEnabled
    );
  const [applewoodRedTheme, setApplewoodRedTheme] = useState(
    DEALERSHIP_CONFIG[getUrlDealershipId()]?.applewoodRedTheme
  );
  const [salesPeople, setSalesPeople] = useState([]);
  useEffect(() => {
    const urlDealershipId = getUrlDealershipId();
    if (urlDealershipId in DEALERSHIP_CONFIG) {
      setDealershipId(urlDealershipId);
      setDealershipName(DEALERSHIP_CONFIG[urlDealershipId].name);
      setDealershipImgUrl(DEALERSHIP_CONFIG[urlDealershipId].imgUrl);
      setLinkMatchesToVdp(DEALERSHIP_CONFIG[urlDealershipId].linkMatchesToVDP);
      setDefaultInventoryImage(
        DEALERSHIP_CONFIG[urlDealershipId].defaultInventoryImage
      );
    }

    const localStorageUserSource = localStorage.getItem(
      "dealership-user-source"
    );

    if (localStorageUserSource === null) {
      const params = new URLSearchParams(location.search);
      const sourceParam = params.get("source");

      if (sourceParam) {
        setUserSource(sourceParam);
        localStorage.setItem("dealership-user-source", sourceParam);
      }
    } else {
      setUserSource(localStorageUserSource);
    }

    const params = new URLSearchParams(location.search);
    const salespersonParam = params.get("sp");

    const localStorageSalesPersonInfo = localStorage.getItem(
      "dealership-salesperson-id"
    );

    if (salespersonParam) {
      const salesPersonInfoTemp =
        DEALERSHIP_CONFIG[urlDealershipId].salesPeople[salespersonParam];
      if (salesPersonInfoTemp) {
        setSalesPersonInfo(salesPersonInfoTemp);
        localStorage.setItem("dealership-salesperson-id", [
          salesPersonInfoTemp.name,
          salesPersonInfoTemp.email,
          salesPersonInfoTemp.oid,
        ]);
      }
    } else if (localStorageSalesPersonInfo !== null) {
      //check if the local storage sales person is in the dealerships
      setSalesPersonInfo({
        name: localStorageSalesPersonInfo.split(",")[0],
        email: localStorageSalesPersonInfo.split(",")[1],
        oid: localStorageSalesPersonInfo.split(",")[2],
      });
    }

    setInitContextFlag(true);
  }, [location, location.pathname, location.search]);

  //salespeople api call
  useEffect(() => {
    if (!dealershipId) return;

    const fetchSalespeople = async () => {
      try {
        const dynamicSalesPeople =
          await getSalespeopleByDealership(dealershipId);

        const salesPeopleWithBlank = [
          { name: "", email: "", oid: "" },
          ...(dynamicSalesPeople || []),
        ];
        setSalesPeople(salesPeopleWithBlank);
        DEALERSHIP_CONFIG[dealershipId].salesPeople = salesPeopleWithBlank;
      } catch (error) {
        console.error("Failed to fetch salespeople:", error);
        setSalesPeople([{ name: "", email: "", oid: "" }]);
      }
    };

    fetchSalespeople();
  }, [dealershipId]);

  const clearDealershipUserSource = () => {
    localStorage.removeItem("dealership-user-source");
  };

  const clearSalesPersonInfo = () => {
    localStorage.removeItem("dealership-salesperson-id");
  };

  const changeSalesPersonInfo = (newValue) => {
    setSalesPersonInfo(newValue);
    localStorage.setItem("dealership-salesperson-id", [
      newValue.name,
      newValue.email,
      newValue.oid,
    ]);
  };
  return (
    <DealershipContext.Provider
      value={{
        dealershipName,
        setDealershipName,
        dealershipImgUrl,
        dealershipId,
        linkMatchesToVDP,
        defaultInventoryImage,
        clearDealershipUserSource,
        clearSalesPersonInfo,
        userSource,
        salesPersonInfo,
        changeSalesPersonInfo,
        initContextFlag,
        testDrivePreApprovalFormEnabled,
        setTestDrivePreApprovalFormEnabled,
        applewoodRedTheme,
        setApplewoodRedTheme,
        baseURL,
        setBaseURL,
        salesPeople,
      }}
    >
      {children}
    </DealershipContext.Provider>
  );
};

export const useDealershipContext = () => {
  return useContext(DealershipContext);
};
